import * as React from "react";
import { cn } from "@/lib/utils";

export type TabsOrientation = "horizontal" | "vertical";

export type TabsListVariant = "underline" | "pill";

interface TabsContextValue {
  value: string;
  setValue: (next: string) => void;
  id: string;
  orientation: TabsOrientation;
  compact: boolean;
  variant: TabsListVariant;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabs(): TabsContextValue {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("Tabs.* deve ser usado dentro de <Tabs>");
  return ctx;
}

interface TabsProps {
  defaultValue?: string;
  value?: string;
  onValueChange?: (next: string) => void;
  children: React.ReactNode;
  className?: string;
}

export function Tabs({
  defaultValue,
  value: controlled,
  onValueChange,
  children,
  className,
}: TabsProps) {
  const [internal, setInternal] = React.useState<string>(defaultValue ?? "");
  const isControlled = controlled !== undefined;
  const value = isControlled ? controlled : internal;
  const id = React.useId();
  const setValue = React.useCallback(
    (next: string) => {
      if (!isControlled) setInternal(next);
      onValueChange?.(next);
    },
    [isControlled, onValueChange]
  );

  return (
    <TabsContext.Provider
      value={{
        value,
        setValue,
        id,
        orientation: "horizontal",
        compact: false,
        variant: "underline",
      }}
    >
      <div className={cn("flex flex-col", className)}>{children}</div>
    </TabsContext.Provider>
  );
}

export interface TabsListProps {
  children: React.ReactNode;
  className?: string;
  "aria-label"?: string;
  orientation?: TabsOrientation;
  compact?: boolean;
  variant?: TabsListVariant;
}

export function TabsList({
  children,
  className,
  "aria-label": ariaLabel,
  orientation = "horizontal",
  compact = false,
  variant = "underline",
}: TabsListProps) {
  const parent = useTabs();
  const listRef = React.useRef<HTMLDivElement | null>(null);

  const ctxValue = React.useMemo<TabsContextValue>(
    () => ({
      ...parent,
      orientation,
      compact: compact && orientation === "vertical",
      variant: orientation === "horizontal" ? variant : "underline",
    }),
    [parent, orientation, compact, variant]
  );

  const onKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const list = listRef.current;
      if (!list) return;
      const triggers = Array.from(
        list.querySelectorAll<HTMLButtonElement>('[role="tab"]:not([aria-disabled="true"])')
      );
      if (triggers.length === 0) return;
      const active = document.activeElement as HTMLElement | null;
      const idx = triggers.findIndex((t) => t === active);
      let next: number | null = null;

      const prevKeys = orientation === "vertical" ? ["ArrowUp"] : ["ArrowLeft"];
      const nextKeys = orientation === "vertical" ? ["ArrowDown"] : ["ArrowRight"];
      if (prevKeys.includes(e.key)) {
        next = idx <= 0 ? triggers.length - 1 : idx - 1;
      } else if (nextKeys.includes(e.key)) {
        next = idx === -1 || idx === triggers.length - 1 ? 0 : idx + 1;
      } else if (e.key === "Home") {
        next = 0;
      } else if (e.key === "End") {
        next = triggers.length - 1;
      }
      if (next !== null) {
        e.preventDefault();
        triggers[next]?.focus();
      }
    },
    [orientation]
  );

  return (
    <TabsContext.Provider value={ctxValue}>
      {/* eslint-disable-next-line jsx-a11y/interactive-supports-focus -- tablist eh container; foco fica nos triggers individuais (cada TabsTrigger tem tabIndex) */}
      <div
        ref={listRef}
        role="tablist"
        aria-orientation={orientation}
        aria-label={ariaLabel}
        onKeyDown={onKeyDown}
        data-orientation={orientation}
        data-compact={compact && orientation === "vertical" ? "true" : "false"}
        className={cn(
          orientation === "horizontal"
            ? variant === "pill"
              ? "inline-flex flex-row items-center gap-0.5 rounded-[11px] border border-border-subtle bg-bg-muted-rd p-1"
              : "inline-flex flex-row items-center justify-start gap-1 overflow-x-auto border-b border-border-subtle"
            : cn(
                "flex shrink-0 flex-col border-r border-border-subtle",
                compact ? "w-[56px]" : "w-[160px]"
              ),
          className
        )}
      >
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  value: string;
  dirty?: boolean;
  icon?: React.ReactNode;
  label?: string;
  badge?: React.ReactNode;
}

export const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  function TabsTrigger(
    { value: tabValue, dirty, icon, label, badge, className, children, ...props },
    ref
  ) {
    const { value, setValue, id, orientation, compact, variant } = useTabs();
    const isActive = value === tabValue;
    const isDisabled = !!props.disabled;
    const isVertical = orientation === "vertical";
    const isPill = !isVertical && variant === "pill";

    const visibleLabel = compact ? null : (children ?? label);
    const ariaLabelComputed =
      props["aria-label"] ??
      label ??
      (compact && typeof children === "string" ? children : undefined);

    return (
      <button
        ref={ref}
        type="button"
        role="tab"
        id={`${id}-tab-${tabValue}`}
        aria-selected={isActive}
        aria-controls={`${id}-panel-${tabValue}`}
        aria-disabled={isDisabled || undefined}
        {...(isVertical && isActive ? { "aria-current": "page" as const } : {})}
        tabIndex={isActive ? 0 : -1}
        data-state={isActive ? "active" : "inactive"}
        title={label}
        aria-label={ariaLabelComputed}
        onClick={() => {
          if (isDisabled) return;
          setValue(tabValue);
        }}
        className={cn(
          "relative whitespace-nowrap transition-colors",
          "focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]",

          !isVertical &&
            !isPill &&
            cn(
              "inline-flex shrink-0 items-center gap-1 border-b-2 px-2.5 py-1.5 text-[12px] font-medium",
              isActive
                ? "border-brand-600 text-text-primary"
                : "border-transparent text-text-secondary hover:text-text-primary"
            ),

          isPill &&
            cn(
              "inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3.5 py-1.5 text-[13px] font-medium",
              isActive
                ? "bg-bg-surface font-semibold text-text-primary [box-shadow:0_1px_2px_rgba(0,0,0,0.05)]"
                : "text-text-secondary hover:text-text-primary"
            ),

          isVertical &&
            !compact &&
            cn(
              "flex flex-row items-center gap-2 px-3 py-2 text-left text-[13px]",
              "font-medium",
              isActive
                ? "bg-brand-50 font-semibold text-text-primary [box-shadow:inset_2px_0_0_var(--brand-500)]"
                : "text-text-secondary hover:bg-bg-muted-rd"
            ),

          isVertical &&
            compact &&
            cn(
              "flex h-[44px] w-full flex-col items-center justify-center",
              isActive
                ? "bg-brand-50 text-text-primary [box-shadow:inset_2px_0_0_var(--brand-500)]"
                : "text-text-secondary hover:bg-bg-muted-rd"
            ),

          isDisabled &&
            "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-text-secondary",
          className
        )}
        {...props}
      >
        {icon ? (
          <span
            className={cn("grid place-items-center", compact ? "h-4 w-4" : "h-4 w-4 shrink-0")}
            aria-hidden="true"
          >
            {icon}
          </span>
        ) : null}
        {visibleLabel ? (
          <span className={cn("truncate", isVertical && !compact && "flex-1")}>{visibleLabel}</span>
        ) : null}
        {badge !== undefined && badge !== null ? (
          <span className={cn(isVertical && compact ? "absolute right-0.5 top-0.5" : "ml-auto")}>
            {badge}
          </span>
        ) : null}
        {dirty ? (
          <span
            aria-label="alterações não salvas"
            className={cn(
              "inline-block rounded-full",
              isVertical && compact
                ? "absolute right-0.5 top-0.5 h-1.5 w-1.5 bg-[var(--rd-warning-500)]"
                : "ml-1 h-1.5 w-1.5 bg-[var(--rd-warning-500)]"
            )}
          />
        ) : null}
      </button>
    );
  }
);

export function TabsContent({
  value: tabValue,
  children,
  className,
}: {
  value: string;
  children: React.ReactNode;
  className?: string;
}) {
  const { value, id } = useTabs();
  const isActive = value === tabValue;
  return (
    <div
      role="tabpanel"
      id={`${id}-panel-${tabValue}`}
      aria-labelledby={`${id}-tab-${tabValue}`}
      tabIndex={0}
      hidden={!isActive}
      data-state={isActive ? "active" : "inactive"}
      className={cn(
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        className
      )}
    >
      {children}
    </div>
  );
}
